import { LandingBlock } from '../types'

/** Miniatura por defecto del vídeo principal. */
export const AVA_VIDEO_THUMBNAIL =
  'https://cdn.magicpatterns.com/patterns/generated-images/b635f2a0-42cc-473c-b571-7ddd6812e6c9.jpg'

/**
 * Textos compartidos por las plantillas de landing.
 * Todo es editable después bloque a bloque desde el constructor.
 */
export const AVA_COPY = {
  eyebrow: 'SISTEMA AVA',
  brandLine: 'ATRAEMOS · VIRALIZAMOS · AUTOMATIZAMOS',
  title: 'Cómo ayudé a pasar de\n100.000 € a 200.000 € al mes\na este negocio de cocinas',
  highlight: '100.000 € a 200.000 €',
  subtitle:
    'En solo 15 días implementamos Sistema AVA, un sistema que combina contenido, publicidad e inteligencia artificial para generar oportunidades de venta todos los días.',
  subtitleHighlight: '15 días',
  ctaLabel: 'Quiero implementar AVA',
  videoOverlay: 'Así funciona\nSistema AVA',
  videoOverlayHighlight: 'Sistema AVA',
  phrase:
    'Diseñado para empresas que quieren crecer\nsin depender del boca a boca',
  calendarTitle: 'Reserva tu consultoría',
  calendarText:
    'Selecciona el día y la hora que mejor te venga para conocer cómo Sistema AVA puede ayudarte.',
  calendarCta: 'Reservar hora',
  formTitle: 'Cuéntanos sobre tu empresa',
  formSubtitle:
    'Responde unas preguntas y analizaremos si tu empresa puede beneficiarse del Sistema AVA antes de programar una reunión.',
  footerText: '© 2026 Sistema AVA. Atracción, Viralidad y Automatización.',
} as const

/** Sección de tres columnas: Atraemos · Viralizamos · Automatizamos. */
export const AVA_PILLARS = [
  {
    icon: 'Magnet',
    title: 'Atraemos',
    text: 'Aplicamos un sistema de captación que genera un flujo constante de potenciales clientes para tu negocio.',
  },
  {
    icon: 'Filter',
    title: 'Viralizamos',
    text: 'Aplicamos una estrategia de contenido viral estratégico para posicionar tu marca como una autoridad y convertirte en la primera opción para tus clientes.',
  },
  {
    icon: 'TrendingUp',
    title: 'Automatizamos',
    text: 'Utilizamos inteligencia artificial para responder y calificar automáticamente a tus clientes potenciales 24/7.',
  },
]

/** Plantillas oficiales. Los tres primeros alias mantienen compatibilidad. */
export type LandingTemplateId =
  | 'caso_exito'
  | 'vsl'
  | 'formulario'
  | 'whatsapp'
  | 'video_calendario'
  | 'con_video'
  | 'solo_formulario'

export interface LandingTemplate {
  id: LandingTemplateId
  name: string
  description: string
}

export const LANDING_TEMPLATES: LandingTemplate[] = [
  {
    id: 'caso_exito',
    name: 'Caso de éxito',
    description:
      'La landing principal: titular de resultado, vídeo protagonista, tres pilares, frase de posicionamiento, formulario y calendario.',
  },
  {
    id: 'vsl',
    name: 'VSL con formulario',
    description:
      'Hero, vídeo, CTA, beneficios, formulario, testimonios y FAQ. El calendario viene oculto.',
  },
  {
    id: 'formulario',
    name: 'Formulario directo',
    description:
      'Logo, título, subtítulo y formulario, con beneficios, testimonios y FAQ opcionales. Sin vídeo.',
  },
  {
    id: 'whatsapp',
    name: 'Landing WhatsApp',
    description:
      'Muy sencilla para campañas rápidas: titular, subtítulo y botón de WhatsApp. Vídeo opcional.',
  },
]

/** Textos propios de la plantilla VSL (sin caso de éxito). */
const VSL_COPY = {
  title: 'En 15 días instalamos un sistema que atrae, filtra y convierte.',
  highlight: 'atrae, filtra y convierte',
}

/** Cabecera minimalista con el logotipo. */
function topbar(
  id: string,
  ctaLabel: string,
  logoAlign: 'left' | 'center' = 'center',
): LandingBlock {
  return {
    id,
    type: 'topbar',
    visible: true,
    spacing: 'normal',
    config: { ctaLabel, sticky: true, showLogo: true, logoHeight: 34, logoAlign },
  }
}

function hero(
  id: string,
  overrides: { title?: string; highlight?: string } = {},
): LandingBlock {
  return {
    id,
    type: 'hero',
    visible: true,
    spacing: 'amplio',
    config: {
      showLogo: false,
      eyebrow: AVA_COPY.eyebrow,
      brandLine: AVA_COPY.brandLine,
      title: overrides.title ?? AVA_COPY.title,
      highlight: overrides.highlight ?? AVA_COPY.highlight,
      subtitle: AVA_COPY.subtitle,
      subtitleHighlight: AVA_COPY.subtitleHighlight,
      extraLine: '',
    },
  }
}

function video(id: string, ctaLabel: string, visible = true): LandingBlock {
  return {
    id,
    type: 'video',
    visible,
    spacing: 'normal',
    config: {
      url: '',
      thumbnailUrl: AVA_VIDEO_THUMBNAIL,
      overlayTitle: AVA_COPY.videoOverlay,
      overlayHighlight: AVA_COPY.videoOverlayHighlight,
      note: '',
      caption: '',
      ctaLabel,
    },
  }
}

function pillars(id: string): LandingBlock {
  return {
    id,
    type: 'pilares',
    visible: true,
    spacing: 'normal',
    config: { items: AVA_PILLARS },
  }
}

function phrase(id: string): LandingBlock {
  return {
    id,
    type: 'frase',
    visible: true,
    spacing: 'normal',
    config: { text: AVA_COPY.phrase },
  }
}

/**
 * Bloque de reserva. Viene OCULTO por defecto en todas las plantillas: solo
 * aparece cuando el usuario lo activa y añade un enlace de reserva válido.
 */
function calendario(id: string, visible = false): LandingBlock {
  return {
    id,
    type: 'calendario',
    visible,
    spacing: 'normal',
    config: {
      title: AVA_COPY.calendarTitle,
      text: AVA_COPY.calendarText,
      // 'global' usa el Google Calendar conectado en Integraciones.
      calendarSource: 'global',
      url: '',
      globalUrl: '',
      // 'boton' abre el enlace; 'incrustado' muestra el calendario dentro.
      calendarMode: 'boton',
      embedHeight: 700,
      ctaLabel: AVA_COPY.calendarCta,
    },
  }
}

function beneficios(id: string, visible = true): LandingBlock {
  return {
    id,
    type: 'beneficios',
    visible,
    spacing: 'normal',
    config: {
      title: 'Qué consigues con el Sistema AVA',
      items: [
        {
          icon: 'Target',
          title: 'Oportunidades cada semana',
          text: 'Un flujo constante de contactos cualificados, no picos aislados.',
        },
        {
          icon: 'Video',
          title: 'Contenido que posiciona',
          text: 'Vídeos pensados para que te elijan antes de hablar contigo.',
        },
        {
          icon: 'Sparkles',
          title: 'Seguimiento automático',
          text: 'La IA responde y cualifica mientras tu equipo vende.',
        },
      ],
    },
  }
}

function testimonios(id: string, visible = true): LandingBlock {
  return {
    id,
    type: 'testimonios',
    visible,
    spacing: 'normal',
    config: {
      title: 'Lo que dicen nuestros clientes',
      items: [
        {
          name: 'Carlos Martínez',
          role: 'Director · Grupo Empresarial XYZ',
          text: 'Pasamos de depender de recomendaciones a tener reuniones agendadas todas las semanas.',
        },
      ],
    },
  }
}

function faq(id: string, visible = true): LandingBlock {
  return {
    id,
    type: 'faq',
    visible,
    spacing: 'normal',
    config: {
      title: 'Preguntas frecuentes',
      items: [
        {
          question: '¿Cuánto tarda en estar en marcha?',
          answer: 'En 15 días dejamos el sistema instalado y funcionando.',
        },
        {
          question: '¿Necesito grabar contenido?',
          answer: 'Definimos juntos el plan y te acompañamos en todo el proceso.',
        },
      ],
    },
  }
}

function formulario(id: string): LandingBlock {
  return {
    id,
    type: 'formulario',
    visible: true,
    spacing: 'amplio',
    config: {
      title: AVA_COPY.formTitle,
      subtitle: AVA_COPY.formSubtitle,
      showProgress: 'si',
      showPrivacy: 'si',
    },
  }
}

/** Bloque de llamada a la acción que abre WhatsApp. */
function whatsappCta(id: string): LandingBlock {
  return {
    id,
    type: 'cta_final',
    visible: true,
    spacing: 'amplio',
    config: {
      title: 'Hablemos por WhatsApp',
      text: 'Escríbenos y te contamos en dos minutos cómo podemos ayudar a tu empresa.',
      ctaLabel: 'Escribir por WhatsApp',
      whatsappNumber: '',
      whatsappMessage: 'Hola, quiero información sobre el Sistema AVA.',
    },
  }
}

/** Botón flotante de WhatsApp. */
function whatsappFloating(id: string): LandingBlock {
  return {
    id,
    type: 'whatsapp',
    visible: true,
    spacing: 'normal',
    config: {
      number: '',
      message: 'Hola, quiero información sobre el Sistema AVA.',
    },
  }
}

function footer(id: string): LandingBlock {
  return {
    id,
    type: 'footer',
    visible: true,
    spacing: 'normal',
    config: {
      text: AVA_COPY.footerText,
      links: ['Política de privacidad', 'Aviso legal', 'Cookies'],
      // Sin URL configurada no se muestra ninguna red social.
      socials: [
        { network: 'instagram', url: '' },
        { network: 'youtube', url: '' },
        { network: 'linkedin', url: '' },
      ],
    },
  }
}

/**
 * Devuelve los bloques de una plantilla. El generador de ids lo aporta quien
 * llama, para que en Supabase sean UUID válidos y en la plantilla estática
 * puedan ser ids legibles.
 */
export function templateBlocks(
  template: LandingTemplateId,
  makeId: () => string,
): LandingBlock[] {
  switch (template) {
    // Landing VSL: vídeo de venta, sin caso de éxito ni frase.
    case 'vsl':
    case 'con_video':
      return [
        topbar(makeId(), AVA_COPY.ctaLabel, 'left'),
        hero(makeId(), VSL_COPY),
        video(makeId(), AVA_COPY.ctaLabel),
        beneficios(makeId()),
        formulario(makeId()),
        testimonios(makeId()),
        faq(makeId()),
        calendario(makeId()),
        footer(makeId()),
      ]

    // Formulario directo: sin vídeo, con secciones de apoyo opcionales.
    case 'formulario':
    case 'solo_formulario':
      return [
        topbar(makeId(), AVA_COPY.ctaLabel, 'left'),
        hero(makeId(), VSL_COPY),
        formulario(makeId()),
        beneficios(makeId()),
        testimonios(makeId()),
        faq(makeId()),
        calendario(makeId()),
        footer(makeId()),
      ]

    // Landing WhatsApp: campaña rápida, sin formulario ni calendario.
    // El vídeo viene incluido pero oculto, para activarlo con un clic.
    case 'whatsapp':
      return [
        topbar(makeId(), '', 'center'),
        hero(makeId(), VSL_COPY),
        video(makeId(), '', false),
        whatsappCta(makeId()),
        whatsappFloating(makeId()),
        footer(makeId()),
      ]

    // Caso de éxito: la landing principal.
    default:
      return [
        topbar(makeId(), ''),
        hero(makeId()),
        video(makeId(), AVA_COPY.ctaLabel),
        pillars(makeId()),
        phrase(makeId()),
        formulario(makeId()),
        calendario(makeId()),
        footer(makeId()),
      ]
  }
}
